import { createPortal } from "react-dom";
import { Tooltip } from "react-tooltip";
import { useEffect, useState } from "react";

import { socketConnection } from "../../util/socket";
import { getOnsiteVisitorCount } from "../../util/clusterService";
import "./TrafficIndicator.scss";

/*
 * Site-level liveness, sitting next to the site name.
 *
 * THE PROBLEM IT SOLVES: when the dashboard's own socket drops - a backend
 * restart, a laptop waking from sleep, a wifi blip, a proxy hiccup - the
 * visualization simply stops moving. Nothing on screen distinguishes "dead
 * pipe" from "quiet afternoon", so someone can sit watching a frozen picture
 * believing it is live. That is the failure this exists to make visible.
 *
 * THREE STATES, and the distinction between the last two is the whole point:
 *   live      - socket up, visitors on site. The only state that animates.
 *   quiet     - socket up, nobody here. CORRECT, and may persist forever;
 *               it is not a problem and must never look like one.
 *   offline   - socket down. We cannot see traffic right now, whatever the
 *               site is actually doing.
 *
 * "No signal" is deliberately the DASHBOARD's own connection, not a
 * time-since-last-event heuristic. A threshold ("no traffic in N days") would
 * cry wolf on a genuinely quiet site and stay silent during a real outage -
 * exactly backwards. Socket state is a real-time truth that flips back the
 * instant the connection recovers.
 *
 * The dot alone carries the state; everything else lives in the hover panel,
 * so the header stays quiet and the site name keeps its position.
 *
 * NOT covered by design: the visitor-side tracking script silently failing
 * while this dashboard's own socket is perfectly healthy. That is a separate
 * problem needing a separate signal - don't be tempted to fold it in here.
 */

const POLL_MS = 2000;

type Status = "live" | "quiet" | "offline";

const COPY: Record<Status, { title: string; detail: string }> = {
  live: {
    title: "Live",
    detail: "Connected and receiving traffic as it happens.",
  },
  quiet: {
    title: "Quiet",
    detail: "Connected and watching. No visitors on the site right now.",
  },
  offline: {
    title: "No signal",
    detail: "Not connected, so live traffic can't reach this dashboard.",
  },
};

const TrafficIndicator = () => {
  const [status, setStatus] = useState<Status>("quiet");
  const [count, setCount] = useState(0);

  useEffect(() => {
    /*
     * Polled rather than event-driven, deliberately.
     *
     * The socket is created by AnalyticsView, not here, so at mount it may not
     * exist yet - binding 'connect'/'disconnect' listeners would mean racing
     * its creation and re-binding whenever it is replaced. Reading the current
     * truth on a timer sidesteps that entirely and cannot miss a transition
     * that happened while we weren't listening.
     *
     * The cost is two property reads every 2s (a boolean and an array length -
     * see getOnsiteVisitorCount), with setState called only when the value
     * actually changes, so an unchanged state re-renders nothing. This is well
     * clear of the per-tick hot path that perf note 5 is about.
     */
    const read = () => {
      const socket = socketConnection();
      const connected = Boolean(socket && socket.connected);
      const onsite = getOnsiteVisitorCount();

      setCount((prev) => (prev === onsite ? prev : onsite));
      setStatus((prev) => {
        const next: Status = !connected ? "offline" : onsite > 0 ? "live" : "quiet";
        return prev === next ? prev : next;
      });
    };

    read();
    const id = window.setInterval(read, POLL_MS);
    return () => window.clearInterval(id);
  }, []);

  const copy = COPY[status];

  /*
   * 🔴 ONE TOOLTIP IMPLEMENTATION IN THE APP, NOT TWO.
   *
   * This used to be a hand-rolled .traffic-indicator-panel: its own dark box,
   * its own arrow, its own z-index, its own show/hide transition. It looked
   * nothing like the react-tooltip used by the right-hand panel and the
   * footer metrics, so the same gesture produced two different visual
   * languages in one header.
   *
   * It also carried every bug the footer conversion already fixed: a bespoke
   * z-index that loses to the drawer, and an absolutely positioned panel that
   * gets clipped by any ancestor with overflow. Rendering through a PORTAL
   * with positionStrategy="fixed" is what removes both, permanently.
   *
   * The content is multi-line, so it goes through `render` rather than
   * data-tooltip-html - no dangerouslySetInnerHTML, and the markup stays
   * type-checked.
   */
  return (
    <>
      <span
        className={`traffic-indicator traffic-indicator--${status} traffic-indicator-anchor`}
        data-tooltip-id="traffic-indicator-tooltip"
        role="status"
        aria-label={`${copy.title}. ${copy.detail}`}
      >
        <span className="traffic-indicator-dot" />
      </span>

      {createPortal(
        <Tooltip
          id="traffic-indicator-tooltip"
          positionStrategy="fixed"
          render={() => (
            <span className="traffic-tip">
              <span className="traffic-tip-title">{copy.title}</span>
              {status !== "offline" && (
                <span className="traffic-tip-count">
                  {count} {count === 1 ? "visitor" : "visitors"} on site
                </span>
              )}
              <span className="traffic-tip-detail">{copy.detail}</span>
            </span>
          )}
        />,
        document.body,
      )}
    </>
  );
};

export default TrafficIndicator;
